import React, { useMemo, useState } from 'react';
import { Link, useForm } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import FileUploader from '@/components/FileUploader';
import useRoutes from '@/hooks/useRoutes';
import usePermissions from '@/hooks/usePermissions';
import { AlertCircle, CheckCircle2, History, Info, Upload } from 'lucide-react';

interface UserField {
    value: string;
    label: string;
}

interface CourseOption {
    id: number;
    title: string;
}

interface UserImportProps {
    user_fields: UserField[];
    courses: CourseOption[];
    course_groups: CourseOption[];
    job_id?: number;
    headers?: string[];
    preview_rows?: string[][];
    started?: boolean;
    errors?: Record<string, string>;
}

const UserImportContent: React.FC<UserImportProps> = ({
    user_fields,
    courses,
    course_groups,
    job_id,
    headers,
    preview_rows,
    started,
    errors: serverErrors,
}) => {
    const { route } = useRoutes();
    const isMappingStep = Boolean(job_id && headers);

    // Step 1 — upload
    const uploadForm = useForm<{ csv_file: File | null }>({ csv_file: null });

    const handleUpload = (e: React.FormEvent) => {
        e.preventDefault();
        uploadForm.post(route('staff-user-import').toString(), {
            forceFormData: true,
            preserveScroll: true,
        });
    };

    // Step 2 — mapping + bulk enrollment
    const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
    const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

    const processForm = useForm({});

    const emailMapped = useMemo(
        () => Object.values(columnMapping).includes('email'),
        [columnMapping]
    );

    const setColumnField = (header: string, field: string) => {
        setColumnMapping((prev) => {
            const next = { ...prev };
            if (field) {
                next[header] = field;
            } else {
                delete next[header];
            }
            return next;
        });
    };

    const toggleId = (
        id: number,
        ids: number[],
        setIds: (value: number[]) => void
    ) => {
        setIds(ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]);
    };

    const toggleAll = (
        items: CourseOption[],
        ids: number[],
        setIds: (value: number[]) => void
    ) => {
        setIds(ids.length === items.length ? [] : items.map((item) => item.id));
    };

    const handleProcess = (e: React.FormEvent) => {
        e.preventDefault();
        processForm.transform(() => ({
            job_id,
            column_mapping: columnMapping,
            course_ids: selectedCourseIds,
            group_ids: selectedGroupIds,
        }));
        processForm.post(route('staff-user-import').toString(), {
            preserveScroll: true,
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-5xl">
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            Importar usuarios
                        </h1>
                        <p className="mt-2 text-gray-600 dark:text-neutral-300">
                            Sube un archivo CSV, mapea las columnas a los campos de
                            usuario y selecciona los cursos a los que se
                            matricularán.
                        </p>
                    </div>
                    <Link href={route('staff-user-import-history').toString()}>
                        <span className="flex items-center whitespace-nowrap text-sm text-gray-600 hover:text-gray-900 dark:text-neutral-300">
                            <History size={16} className="mr-1" />
                            Ver historial
                        </span>
                    </Link>
                </div>

                {started && (
                    <div className="mb-6 flex items-start rounded-lg border border-green-200 bg-green-50 p-4">
                        <CheckCircle2
                            size={20}
                            className="mr-3 mt-0.5 flex-shrink-0 text-green-600"
                        />
                        <div className="text-sm text-green-800">
                            <p className="font-semibold">
                                Importación iniciada.
                            </p>
                            <p>
                                Se está procesando en segundo plano. Recibirás una
                                notificación con el informe cuando termine.
                            </p>
                        </div>
                    </div>
                )}

                {serverErrors?.mapping && (
                    <div className="mb-6 flex items-center rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        <AlertCircle size={16} className="mr-2" />
                        {serverErrors.mapping}
                    </div>
                )}

                {!isMappingStep && (
                    <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                            <div className="flex">
                                <Info
                                    size={20}
                                    className="mr-3 mt-0.5 flex-shrink-0 text-blue-600"
                                />
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold">Sobre la importación:</p>
                                    <ul className="ml-4 mt-2 list-disc space-y-1">
                                        <li>
                                            Los usuarios nuevos se crearán y recibirán
                                            sus credenciales por correo.
                                        </li>
                                        <li>
                                            Los emails ya existentes se matricularán en
                                            los cursos seleccionados.
                                        </li>
                                        <li>
                                            La importación se ejecuta en segundo plano y
                                            te notificaremos al terminar.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleUpload} className="space-y-6">
                            <FileUploader
                                accept=".csv"
                                selectedFile={uploadForm.data.csv_file}
                                onFileSelect={(file) =>
                                    uploadForm.setData('csv_file', file)
                                }
                            />
                            {serverErrors?.csv_file && (
                                <div className="flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    {serverErrors.csv_file}
                                </div>
                            )}
                            <div className="flex justify-end">
                                <PrimaryButton
                                    type="submit"
                                    disabled={
                                        !uploadForm.data.csv_file ||
                                        uploadForm.processing
                                    }
                                >
                                    <Upload size={16} className="mr-2" />
                                    {uploadForm.processing
                                        ? 'Cargando...'
                                        : 'Cargar y previsualizar'}
                                </PrimaryButton>
                            </div>
                        </form>
                    </div>
                )}

                {isMappingStep && (
                    <form onSubmit={handleProcess} className="space-y-6">
                        {/* Preview */}
                        <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Vista previa
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-neutral-700">
                                    <thead>
                                        <tr>
                                            {headers?.map((header) => (
                                                <th
                                                    key={header}
                                                    className="px-3 py-2 text-left font-medium text-gray-500 dark:text-neutral-300"
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                                        {preview_rows?.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {headers?.map((header, colIndex) => (
                                                    <td
                                                        key={header}
                                                        className="px-3 py-2 text-gray-700 dark:text-neutral-300"
                                                    >
                                                        {row[colIndex] ?? ''}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Column mapping */}
                        <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Mapeo de columnas
                            </h2>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {headers?.map((header) => (
                                    <div key={header}>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                            {header}
                                        </label>
                                        <select
                                            value={columnMapping[header] || ''}
                                            onChange={(e) =>
                                                setColumnField(header, e.target.value)
                                            }
                                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-neutral-600 sm:text-sm"
                                        >
                                            <option value="">-- Ignorar --</option>
                                            {user_fields.map((field) => (
                                                <option
                                                    key={field.value}
                                                    value={field.value}
                                                >
                                                    {field.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                            {!emailMapped && (
                                <div className="mt-4 flex items-center text-sm text-red-600">
                                    <AlertCircle size={16} className="mr-1" />
                                    Debes mapear una columna al campo Email.
                                </div>
                            )}
                        </div>

                        {/* Bulk enrollment */}
                        <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Matricular en cursos
                            </h2>
                            {courses.length === 0 && course_groups.length === 0 && (
                                <p className="text-sm text-gray-500 dark:text-neutral-400">
                                    No hay cursos disponibles en esta academia.
                                </p>
                            )}
                            {course_groups.length > 0 && (
                                <div className="mb-6">
                                    <div className="mb-2 flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-neutral-300">
                                            Grupos de cursos
                                        </h3>
                                        <label className="flex items-center text-xs font-medium text-gray-600 dark:text-neutral-400">
                                            <input
                                                type="checkbox"
                                                className="mr-2 rounded border-gray-300"
                                                checked={
                                                    selectedGroupIds.length ===
                                                    course_groups.length
                                                }
                                                onChange={() =>
                                                    toggleAll(
                                                        course_groups,
                                                        selectedGroupIds,
                                                        setSelectedGroupIds
                                                    )
                                                }
                                            />
                                            Seleccionar todo
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {course_groups.map((group) => (
                                            <label
                                                key={group.id}
                                                className="flex items-center text-sm text-gray-700 dark:text-neutral-300"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="mr-2 rounded border-gray-300"
                                                    checked={selectedGroupIds.includes(
                                                        group.id
                                                    )}
                                                    onChange={() =>
                                                        toggleId(
                                                            group.id,
                                                            selectedGroupIds,
                                                            setSelectedGroupIds
                                                        )
                                                    }
                                                />
                                                {group.title}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {courses.length > 0 && (
                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-neutral-300">
                                            Cursos individuales
                                        </h3>
                                        <label className="flex items-center text-xs font-medium text-gray-600 dark:text-neutral-400">
                                            <input
                                                type="checkbox"
                                                className="mr-2 rounded border-gray-300"
                                                checked={
                                                    selectedCourseIds.length ===
                                                    courses.length
                                                }
                                                onChange={() =>
                                                    toggleAll(
                                                        courses,
                                                        selectedCourseIds,
                                                        setSelectedCourseIds
                                                    )
                                                }
                                            />
                                            Seleccionar todo
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {courses.map((course) => (
                                            <label
                                                key={course.id}
                                                className="flex items-center text-sm text-gray-700 dark:text-neutral-300"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="mr-2 rounded border-gray-300"
                                                    checked={selectedCourseIds.includes(
                                                        course.id
                                                    )}
                                                    onChange={() =>
                                                        toggleId(
                                                            course.id,
                                                            selectedCourseIds,
                                                            setSelectedCourseIds
                                                        )
                                                    }
                                                />
                                                {course.title}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3">
                            <SecondaryButton
                                type="button"
                                onClick={() =>
                                    uploadForm.get(route('staff-user-import').toString())
                                }
                            >
                                Cancelar
                            </SecondaryButton>
                            <PrimaryButton
                                type="submit"
                                disabled={!emailMapped || processForm.processing}
                            >
                                {processForm.processing
                                    ? 'Iniciando...'
                                    : 'Iniciar importación'}
                            </PrimaryButton>
                        </div>
                    </form>
                )}
            </div>
        </StaffLayout>
    );
};

const UserImport: React.FC<UserImportProps> = (props) => {
    const { canImportUsers } = usePermissions();

    if (!canImportUsers) {
        return (
            <ThemeWrapper>
                <StaffLayout>
                    <div className="mx-auto max-w-3xl py-12 text-center">
                        <AlertCircle
                            size={40}
                            className="mx-auto mb-4 text-red-500"
                        />
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            No tienes permiso para importar usuarios
                        </h1>
                    </div>
                </StaffLayout>
            </ThemeWrapper>
        );
    }

    return (
        <ThemeWrapper>
            <UserImportContent {...props} />
        </ThemeWrapper>
    );
};

export default UserImport;
